import { useEffect, useMemo, useState } from 'react';

import { usePreferences } from '@/react-query/usePreferences';
import { usePrompts } from '@/react-query/videa-notes/usePrompts';
import { useVoiceNoteTemplates } from '@/react-query/videa-notes/useVoiceNoteTemplates';

import type { VoiceNoteTemplateOption } from '../types';

/**
 * Hook for managing prompt and template selection in Videa Notes.
 * Handles loading options from the API and auto-selecting the first option.
 */
export const usePromptTemplateSelection = () => {
  const { data: promptOptions } = usePrompts();
  const { data: voiceNoteTemplates = [] } = useVoiceNoteTemplates();
  const { data: preferences } = usePreferences();
  const voiceNotesTemplatesConfig = preferences?.voiceNotesTemplatesConfig;

  const [selectedPrompt, setSelectedPrompt] = useState<string | undefined>(
    undefined,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    undefined,
  );

  const templateOptions: VoiceNoteTemplateOption[] = useMemo(
    () =>
      voiceNoteTemplates.map((template) => ({
        id: template.id,
        name: template.name,
        type: template.type,
      })),
    [voiceNoteTemplates],
  );

  // Auto-select the first prompt when promptOptions loads from the API
  useEffect(() => {
    if (Array.isArray(promptOptions) && promptOptions.length > 0) {
      setSelectedPrompt(promptOptions[0].name);
    }
  }, [promptOptions]);

  // Auto-select the first template when templateOptions loads from the API
  useEffect(() => {
    if (templateOptions.length === 0) return;

    const defaultId = voiceNotesTemplatesConfig?.defaultTemplateId;
    const fallbackId = templateOptions[0].id;

    const selectedId =
      defaultId && templateOptions.find((t) => t.id === defaultId)?.id
        ? defaultId
        : fallbackId;

    setSelectedTemplate((prev) => (prev === selectedId ? prev : selectedId));
  }, [templateOptions, voiceNotesTemplatesConfig]);

  return {
    promptOptions: promptOptions ?? [],
    selectedPrompt,
    setSelectedPrompt,
    templateOptions,
    selectedTemplate,
    setSelectedTemplate,
  };
};
